import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { StrategyMonth, StrategyProgress } from "@breeze/shared";
import { fetchStrategyProgress } from "../api.js";
import { Section } from "./BusinessGrowth.js";

// ===========================================================================
// Strategy progress widget — the runway-tracking chart + table backing
// STRATEGY.md. Shows monthly profit vs household-need + Mimecast-
// replacement reference lines so Darren can see whether Breeze profit
// is converging on the runway moment.
// ===========================================================================

function formatGBP(amount: number, compact = false): string {
  const abs = Math.abs(amount);
  if (compact && abs >= 1000) {
    return `${amount < 0 ? "−" : ""}£${(abs / 1000).toFixed(abs >= 10_000 ? 0 : 1)}k`;
  }
  return `${amount < 0 ? "−" : ""}£${Math.round(abs).toLocaleString("en-GB")}`;
}

function shortMonthLabel(ym: string): string {
  const parts = ym.split("-");
  const y = parts[0] ?? "0000";
  const m = parts[1] ?? "00";
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[Number(m) - 1]} '${y.slice(2)}`;
}

export function StrategyProgressSection(): JSX.Element {
  const q = useQuery<StrategyProgress>({
    queryKey: ["business-growth", "strategy-progress"],
    queryFn: fetchStrategyProgress,
    staleTime: 5 * 60_000,
  });

  if (q.isLoading) {
    return (
      <Section title="Runway progress" subtitle="Loading…">
        <div className="text-[14px] text-ink-100">Loading…</div>
      </Section>
    );
  }
  if (q.isError || !q.data) {
    return (
      <Section title="Runway progress" subtitle="Goal vs actual — read STRATEGY.md for context">
        <div className="rounded-md border border-stage-lost/40 bg-stage-lost/10 p-3 text-[14px] text-stage-lost">
          {q.error instanceof Error ? q.error.message : "Couldn't load progress"}
        </div>
      </Section>
    );
  }

  const p = q.data;
  const taxRate = p.goal.ownerExtractionTaxRate;
  // Convert NET household targets into pre-tax (operating profit) lines
  // for the chart, since the profit number we plot is pre-owner-extraction.
  const householdLine = Math.round(p.goal.householdMonthlyNeed / (1 - taxRate));
  const mimecastLine = Math.round(p.goal.mimecastReplacementNet / (1 - taxRate));

  // Chart rows — monthly profit, plus a target-profit dashed line for
  // goal-period months (null pre-goal so the line doesn't appear before
  // there was a target).
  const chartRows = p.months.map((m: StrategyMonth) => ({
    label: shortMonthLabel(m.month),
    profit: m.profit,
    targetProfit: m.targetProfit,
    isProjection: m.isProjection,
  }));

  // Latest actual + projected-annual summary for tiles.
  const latestActual = [...p.months].reverse().find((m) => !m.isProjection);
  const latestActualProfit = latestActual?.profit ?? 0;
  const latestActualLabel = latestActual ? shortMonthLabel(latestActual.month) : "—";

  return (
    <Section
      title="Runway progress"
      subtitle={
        "Profit per month vs household-need (red) and Mimecast-replacement (yellow) reference lines. " +
        "Profit is operating profit BEFORE owner PAYE/NI/pension — household-need line already factors in " +
        `~${Math.round(taxRate * 100)}% extraction tax. ` +
        (p.totals.vatTriggerMonth
          ? `VAT (Flat Rate 12%) kicks in ${shortMonthLabel(p.totals.vatTriggerMonth)}.`
          : "Not yet VAT-registered.")
      }
    >
      {/* Tiles ---------------------------------------------------------- */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Tile
          label="Latest actual profit"
          value={formatGBP(latestActualProfit)}
          hint={`for ${latestActualLabel}`}
          accent={latestActualProfit >= householdLine ? "text-stage-won" : latestActualProfit >= mimecastLine ? "text-stage-lead" : "text-stage-opportunity"}
        />
        <Tile
          label="Projected annual profit"
          value={formatGBP(p.totals.projectedAnnualProfit)}
          hint={`goal year May'26→Apr'27 · ${Math.round((p.totals.projectedAnnualProfit / 12 / householdLine) * 100)}% of household need`}
        />
        <Tile
          label="Reliable techs"
          value={`${p.techs.activeNow} / ${p.techs.target}`}
          hint="active last 30 days"
          accent={p.techs.activeNow >= p.techs.target ? "text-stage-won" : "text-stage-opportunity"}
        />
        <Tile
          label="Runway target"
          value={formatGBP(p.goal.runwayTargetRevenue)}
          hint={`24-36mo · ${p.goal.runwayTargetTechs} techs · £6k/mo to household`}
        />
      </div>

      {/* Chart ---------------------------------------------------------- */}
      <div className="mt-5 h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartRows} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#3a3340" strokeDasharray="3 3" />
            <XAxis
              dataKey="label"
              stroke="#9CA3AF"
              tick={{ fontSize: 11, fill: "#cbd5e1" }}
              tickLine={false}
            />
            <YAxis
              stroke="#9CA3AF"
              tick={{ fontSize: 11, fill: "#cbd5e1" }}
              tickFormatter={(v: number) => formatGBP(v, true)}
              width={56}
            />
            <Tooltip
              contentStyle={{
                background: "#1f1b26",
                border: "1px solid #4A154B",
                borderRadius: 8,
                color: "#f5f0fa",
                fontSize: 12,
              }}
              formatter={(v, name) => {
                if (v === null || v === undefined) return ["—", name];
                return [formatGBP(Number(v)), name];
              }}
            />
            <Bar
              dataKey="profit"
              name="Profit (monthly)"
              fill="#22d3ee"
              fillOpacity={0.85}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="targetProfit"
              name="Target profit"
              stroke="#A78BFA"
              strokeWidth={2}
              strokeDasharray="6 4"
              dot={false}
              connectNulls={false}
              isAnimationActive={false}
            />
            <ReferenceLine
              y={householdLine}
              stroke="#ef4444"
              strokeDasharray="4 4"
              strokeWidth={2}
              label={{
                value: `Household need: ${formatGBP(householdLine, true)}/mo profit (=£6k net)`,
                position: "insideTopRight",
                fill: "#ef4444",
                fontSize: 11,
              }}
            />
            <ReferenceLine
              y={mimecastLine}
              stroke="#facc15"
              strokeDasharray="4 4"
              strokeWidth={2}
              label={{
                value: `Mimecast replacement: ${formatGBP(mimecastLine, true)}/mo profit (=£3.6k net)`,
                position: "insideBottomRight",
                fill: "#facc15",
                fontSize: 11,
              }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Table ---------------------------------------------------------- */}
      <div className="mt-6 overflow-x-auto">
        <table className="w-full min-w-[920px] text-[12px] tabular-nums">
          <thead>
            <tr className="border-b border-ink-700 text-[11px] uppercase tracking-[0.12em] text-ink-100">
              <th className="py-2 pr-3 text-left font-semibold">Month</th>
              <th className="px-2 text-right font-semibold">Techs</th>
              <th className="px-2 text-right font-semibold">Jobs</th>
              <th className="px-2 text-right font-semibold">Sales</th>
              <th className="px-2 text-right font-semibold">VAT</th>
              <th className="px-2 text-right font-semibold">Net</th>
              <th className="px-2 text-right font-semibold">Cleaners</th>
              <th className="px-2 text-right font-semibold">Costs</th>
              <th className="px-2 text-right font-semibold">Profit</th>
              <th className="px-2 text-right font-semibold">Target</th>
              <th className="pl-2 text-right font-semibold">Δ vs target</th>
            </tr>
          </thead>
          <tbody>
            {p.months.map((m) => {
              const delta =
                m.targetProfit !== null ? m.profit - m.targetProfit : null;
              const deltaColor =
                delta === null
                  ? "text-ink-100"
                  : delta >= 0
                    ? "text-stage-won"
                    : delta >= -500
                      ? "text-stage-opportunity"
                      : "text-stage-lost";
              return (
                <tr
                  key={m.month}
                  className={`border-b border-ink-700/60 ${m.isProjection ? "opacity-70" : ""}`}
                  title={m.isProjection ? "Projection — not actual data" : undefined}
                >
                  <td className="py-1.5 pr-3 text-ink-50">
                    {shortMonthLabel(m.month)}
                    {m.isProjection && (
                      <span className="ml-1.5 rounded-sm bg-ink-700 px-1 py-0.5 text-[9px] uppercase tracking-wider text-ink-100">
                        proj
                      </span>
                    )}
                    {m.vatRegistered && (
                      <span className="ml-1.5 rounded-sm bg-stage-opportunity/30 px-1 py-0.5 text-[9px] uppercase tracking-wider text-stage-opportunity">
                        VAT
                      </span>
                    )}
                  </td>
                  <td className="px-2 text-right text-ink-50">{m.techs}</td>
                  <td className="px-2 text-right text-ink-50">{m.jobs}</td>
                  <td className="px-2 text-right text-ink-50">{formatGBP(m.grossSales)}</td>
                  <td className="px-2 text-right text-ink-100">
                    {m.vatDue > 0 ? `−${formatGBP(m.vatDue)}` : "—"}
                  </td>
                  <td className="px-2 text-right text-ink-50">{formatGBP(m.netSales)}</td>
                  <td className="px-2 text-right text-ink-100">
                    {m.cleanerPay > 0 ? `−${formatGBP(m.cleanerPay)}` : "—"}
                  </td>
                  <td className="px-2 text-right text-ink-100">
                    {m.otherExpenses > 0 ? `−${formatGBP(m.otherExpenses)}` : "—"}
                  </td>
                  <td className={`px-2 text-right font-semibold ${m.profit >= householdLine ? "text-stage-won" : m.profit >= mimecastLine ? "text-stage-lead" : m.profit >= 0 ? "text-stage-opportunity" : "text-stage-lost"}`}>
                    {formatGBP(m.profit)}
                  </td>
                  <td className="px-2 text-right text-ink-100">
                    {m.targetProfit !== null ? formatGBP(m.targetProfit) : "—"}
                  </td>
                  <td className={`pl-2 text-right ${deltaColor}`}>
                    {delta === null ? "—" : (delta >= 0 ? "+" : "") + formatGBP(delta)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-3 text-[11px] leading-relaxed text-ink-100">
        <strong className="text-ink-50">Reading this:</strong>{" "}
        Past months use real data (B_TVISIT + cleaner statements + B_TEXPENSE).{" "}
        Projection months ramp toward the £110k 12-month goal using the trailing-12 cleaner-pay ratio and average other costs.{" "}
        VAT is modelled under the Flat Rate Scheme at 12% of gross once rolling-12 sales crosses £90k.{" "}
        "Costs" includes Andzelika's wages and any other payroll already on B_TEXPENSE.{" "}
        Profit is operating profit BEFORE Darren's PAYE/NI/pension — the red household-need line ({formatGBP(householdLine)}/mo) already grosses up the £6k net for that.
      </div>
    </Section>
  );
}

function Tile({
  label,
  value,
  hint,
  accent,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: string;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-ink-700 bg-ink-700/40 p-3">
      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-ink-100">
        {label}
      </div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${accent ?? "text-ink-50"}`}>
        {value}
      </div>
      {hint && <div className="mt-0.5 text-[11px] text-ink-100">{hint}</div>}
    </div>
  );
}
